/**
 * L7 Platform — progressive logo-ribbon enhancement for L7 Logo Carousel.
 *
 * Duplicates `.l7-logo-carousel__track` children until the ribbon is at
 * least two container-widths wide (always at least once, always an even
 * number of sets) and activates a CSS `translateX(-50%)` marquee. Clones
 * are `aria-hidden` and removed from the tab order. Without JS the logos
 * render as a wrapping static row — no content loss. Never mutates the
 * Visual Builder DOM (`body.et-fb`). Hover / `:focus-within` pause the
 * strip. The published ribbon stays a ribbon even when the OS has
 * Reduce Motion on.
 */
(function (window, document) {
	'use strict';

	var SELECTOR = '.l7-logo-carousel';
	var DEFAULT_SPEED = 40;
	var MIN_SPEED = 10;
	var MAX_SPEED = 120;

	/**
	 * @return {boolean}
	 */
	function isVisualBuilder() {
		return document.body && document.body.classList.contains('et-fb');
	}

	/**
	 * Loop duration from `data-l7-speed` (seconds). Missing or invalid
	 * values fall back to 40. Out-of-range numbers are clamped to 10–120.
	 *
	 * @param {Element} root `.l7-logo-carousel`.
	 * @return {number}
	 */
	function readSpeed(root) {
		var raw = root.getAttribute('data-l7-speed');
		if (raw == null || raw === '') {
			return DEFAULT_SPEED;
		}
		var seconds = parseInt(raw, 10);
		if (!isFinite(seconds) || seconds < 1) {
			return DEFAULT_SPEED;
		}
		if (seconds < MIN_SPEED) {
			return MIN_SPEED;
		}
		if (seconds > MAX_SPEED) {
			return MAX_SPEED;
		}
		return seconds;
	}

	/**
	 * Collect the authored track children (Divi wrappers or item roots).
	 *
	 * @param {Element} track `.l7-logo-carousel__track`.
	 * @return {Element[]}
	 */
	function collectItems(track) {
		var wrappers = track.querySelectorAll(
			':scope > .et_pb_module.l7_logo_carousel_item, :scope > .l7_logo_carousel_item, :scope > .l7-logo-carousel__item'
		);
		if (wrappers.length) {
			return Array.prototype.slice.call(wrappers);
		}
		return Array.prototype.slice.call(track.children);
	}

	/**
	 * Hide a cloned item from AT and the tab order. The duplicate stays
	 * visible in the ribbon; keyboard users only reach the authored set.
	 *
	 * @param {Element} clone Duplicated track child.
	 * @return {void}
	 */
	function neutralizeClone(clone) {
		clone.setAttribute('aria-hidden', 'true');
		if ('inert' in clone) {
			clone.inert = true;
		}
		var focusable = clone.querySelectorAll('a[href]');
		var i;
		for (i = 0; i < focusable.length; i++) {
			focusable[i].setAttribute('tabindex', '-1');
		}
	}

	/**
	 * Append one copy of the authored item set.
	 *
	 * @param {Element} track `.l7-logo-carousel__track`.
	 * @param {Element[]} items Authored children.
	 * @return {void}
	 */
	function appendClonedSet(track, items) {
		var i;
		for (i = 0; i < items.length; i++) {
			var clone = items[i].cloneNode(true);
			neutralizeClone(clone);
			track.appendChild(clone);
		}
	}

	/**
	 * Enhance a single logo carousel into an infinite ribbon.
	 *
	 * @param {Element} root `.l7-logo-carousel`.
	 * @return {void}
	 */
	function enhance(root) {
		if (root.getAttribute('data-l7-logo-carousel') === '1') {
			return;
		}

		var track = root.querySelector('.l7-logo-carousel__track');
		if (!track) {
			return;
		}

		var items = collectItems(track);
		if (!items.length) {
			return;
		}

		appendClonedSet(track, items);

		root.style.setProperty('--l7-logo-speed', readSpeed(root) + 's');
		root.classList.add('l7-logo-carousel--enhanced');
		root.setAttribute('data-l7-logo-carousel', '1');

		var extraSets = 0;
		var maxExtraSets = 6;
		while (
			track.scrollWidth < 2 * root.clientWidth &&
			extraSets < maxExtraSets
		) {
			appendClonedSet(track, items);
			appendClonedSet(track, items);
			extraSets += 2;
		}

		hideBrokenImages(root);
	}

	/**
	 * Item wrapper to hide when a logo file is missing. Must not walk up to
	 * the parent `.et_pb_module.l7_logo_carousel` — that collapsed the
	 * whole homepage ribbon after the first 404 or not-yet-loaded lazy img.
	 *
	 * @param {Element} img Logo `<img>`.
	 * @return {Element|null}
	 */
	function brokenImageWrap(img) {
		return (
			img.closest('.l7-logo-carousel__item') ||
			img.closest('.et_pb_module.l7_logo_carousel_item') ||
			img.closest('.l7_logo_carousel_item')
		);
	}

	/**
	 * Hide a logo whose file 404s so the ribbon does not show a broken-image
	 * icon. Binds after cloning so duplicates are covered too.
	 *
	 * @param {Element} root `.l7-logo-carousel`.
	 * @return {void}
	 */
	function hideBrokenImages(root) {
		var imgs = root.querySelectorAll('.l7-logo-carousel__item img');
		var i;
		for (i = 0; i < imgs.length; i++) {
			(function (img) {
				function hide() {
					var wrap = brokenImageWrap(img);
					if (wrap) {
						wrap.style.display = 'none';
					}
				}
				img.addEventListener('error', hide);
				// Lazy images that have not started a fetch can report
				// complete + naturalWidth 0. currentSrc is only set after
				// the browser actually requests the file.
				if (
					img.complete &&
					img.naturalWidth === 0 &&
					img.getAttribute('src') &&
					img.currentSrc
				) {
					hide();
				}
			})(imgs[i]);
		}
	}

	/**
	 * @return {void}
	 */
	function init() {
		if (isVisualBuilder()) {
			return;
		}

		var roots = document.querySelectorAll(SELECTOR);
		var i;
		for (i = 0; i < roots.length; i++) {
			enhance(roots[i]);
		}
	}

	if (document.readyState === 'loading') {
		document.addEventListener('DOMContentLoaded', init);
	} else {
		init();
	}
})(window, document);
